/**
 * Hook for managing multi-date selection mode
 * Supports selecting specific non-consecutive dates
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DateStorageService } from '../services/DateStorageService';
import { DateValidationService } from '../services/DateValidationService';
import { useAdminBypass } from '@/contexts/AdminBypassContext';

export type DateSelectionMode = 'range' | 'specific';

interface UseMultiDateSelectionReturn {
  selectedDates: Date[];
  toggleDate: (date: Date) => void;
  addDate: (date: Date) => void;
  removeDate: (date: Date) => void;
  clearAllDates: () => void;
  selectAllAvailableDates: (month: Date) => void;
  isDateSelected: (date: Date) => boolean;
  minDate: Date;
}

const STORAGE_KEY = 'selectedDates';

/**
 * Normalize a date to midnight for consistent comparison
 */
const normalizeDate = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Check if two dates are the same day
 */
const isSameDay = (a: Date, b: Date): boolean => {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
};

export const useMultiDateSelection = (
  onChange: (value: number[]) => void,
  isActive: boolean = true
): UseMultiDateSelectionReturn => {
  const { canBypassDateRestrictions, checking } = useAdminBypass();
  
  // Use ref to lock minDate once admin check completes - prevents mid-session changes
  const minDateRef = useRef<Date | null>(null);
  
  // Stabilize minDate - only set once when checking completes
  const minDate = useMemo(() => {
    // Once we have a locked value, always return it
    if (minDateRef.current) {
      return minDateRef.current;
    }
    
    // While checking, use a conservative default (48 hours from now)
    if (checking) {
      const defaultMin = new Date();
      defaultMin.setHours(defaultMin.getHours() + 48);
      return defaultMin;
    }
    
    // First time checking completes - lock the value
    const calculatedMin = DateValidationService.getMinimumDate(canBypassDateRestrictions);
    minDateRef.current = calculatedMin;
    return calculatedMin;
  }, [canBypassDateRestrictions, checking]);
  
  // Use ref for minDateNormalized to prevent callback recreation
  const minDateNormalizedRef = useRef(normalizeDate(minDate));
  
  // Update ref when minDate changes (but don't cause callback recreation)
  useEffect(() => {
    minDateNormalizedRef.current = normalizeDate(minDate);
  }, [minDate]);
  
  // Use ref for onChange to avoid re-render loops
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Load initial dates from storage with lazy initialization
  const [selectedDates, setSelectedDates] = useState<Date[]>(() => {
    if (!isActive) return []; // Don't load if not active
    
    const storedDates = localStorage.getItem(STORAGE_KEY);
    if (!storedDates) return [];
    
    // Use a conservative minDate for initial load (48 hours)
    const initialMinDate = new Date();
    initialMinDate.setHours(initialMinDate.getHours() + 48);
    const initialMinNormalized = normalizeDate(initialMinDate);
    
    try {
      const parsed = JSON.parse(storedDates);
      return parsed
        .map((dateStr: string) => new Date(dateStr))
        .filter((d: Date) => {
          if (isNaN(d.getTime())) return false;
          const dateNormalized = normalizeDate(d);
          return dateNormalized >= initialMinNormalized;
        });
    } catch {
      return [];
    }
  });

  // Debounced persistence effect - prevents excessive storage writes
  useEffect(() => {
    if (!isActive) return; // Skip if not active mode
    
    // Debounce the persistence to batch rapid changes
    const timeoutId = setTimeout(() => {
      if (selectedDates.length > 0) {
        // Save to localStorage and formData
        DateStorageService.saveMultiDates(selectedDates);
        
        // Explicitly save selection mode for useCampaignDays to detect
        localStorage.setItem('dateSelectionMode', 'specific');
        
        // Also update start/end dates for backward compatibility (silent to avoid triggering range hook)
        const sortedDates = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
        DateStorageService.saveDate(sortedDates[0], 'start', { silent: true });
        DateStorageService.saveDate(sortedDates[sortedDates.length - 1], 'end', { silent: true });
        
        // Update duration to number of selected dates
        onChangeRef.current([selectedDates.length]);
      } else {
        DateStorageService.clearMultiDates();
        // Keep 'dateSelectionMode' = 'specific' while user is actively in specific mode,
        // even if they momentarily have 0 dates selected. Mode is cleared explicitly
        // when the user toggles back to range mode.
        onChangeRef.current([0]);
      }

    }, 50); // 50ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [selectedDates, isActive]);

  // Check if a date is selected
  const isDateSelected = useCallback((date: Date): boolean => {
    return selectedDates.some(d => isSameDay(d, date));
  }, [selectedDates]);

  // Toggle a date selection - stable callback using refs
  const toggleDate = useCallback((date: Date) => {
    // Normalize dates for comparison using ref
    const dateNormalized = normalizeDate(date);
    
    if (dateNormalized < minDateNormalizedRef.current) return;
    
    setSelectedDates(prev => {
      const exists = prev.some(d => isSameDay(d, date));
      
      if (exists) {
        return prev.filter(d => !isSameDay(d, date));
      } else {
        // Store date with noon time to avoid timezone issues
        const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
        return [...prev, newDate].sort((a, b) => a.getTime() - b.getTime());
      }
    });
  }, []); // No dependencies - uses refs for stability

  // Add a date - stable callback using refs
  const addDate = useCallback((date: Date) => {
    const dateNormalized = normalizeDate(date);
    if (dateNormalized < minDateNormalizedRef.current) return;
    
    setSelectedDates(prev => {
      const exists = prev.some(d => isSameDay(d, date));
      if (exists) return prev;
      
      const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
      return [...prev, newDate].sort((a, b) => a.getTime() - b.getTime());
    });
  }, []); // No dependencies - uses refs

  // Remove a date
  const removeDate = useCallback((date: Date) => {
    setSelectedDates(prev => prev.filter(d => !isSameDay(d, date)));
  }, []);

  // Clear all dates
  const clearAllDates = useCallback(() => {
    setSelectedDates([]);
    localStorage.removeItem(STORAGE_KEY);
    DateStorageService.clearMultiDates();
  }, []);

  // Select all available dates in a given month - stable callback using refs
  const selectAllAvailableDates = useCallback((month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    
    const newDates: Date[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day, 12, 0, 0);
      const dateNormalized = normalizeDate(date);
      if (dateNormalized >= minDateNormalizedRef.current) {
        newDates.push(date);
      }
    }
    
    setSelectedDates(prev => {
      const combined = [...prev];
      newDates.forEach(newDate => {
        const exists = combined.some(d => isSameDay(d, newDate));
        if (!exists) {
          combined.push(newDate);
        }
      });
      return combined.sort((a, b) => a.getTime() - b.getTime());
    });
  }, []); // No dependencies - uses refs

  return {
    selectedDates,
    toggleDate,
    addDate,
    removeDate,
    clearAllDates,
    selectAllAvailableDates,
    isDateSelected,
    minDate
  };
};
